import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { featureRoadmapVerdictsKeys } from "@/application/query-keys/feature-roadmap-verdicts.keys";
import {
  featureRoadmapVerdictsRepository,
  type UpsertVerdictInput,
} from "@/infrastructure/repositories/feature-roadmap-verdicts.supabase.repository";
import type {
  FeatureRoadmapVerdictRow,
  RoadmapBranchValue,
} from "@/infrastructure/supabase/database.types";

export function useAllVerdicts() {
  return useQuery({
    queryKey: featureRoadmapVerdictsKeys.list(),
    queryFn: () => featureRoadmapVerdictsRepository.list(),
  });
}

export function useVerdictsByBranch(branch: RoadmapBranchValue) {
  return useQuery({
    queryKey: featureRoadmapVerdictsKeys.byBranch(branch),
    queryFn: () => featureRoadmapVerdictsRepository.listByBranch(branch),
  });
}

export function useUpsertVerdict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertVerdictInput) => featureRoadmapVerdictsRepository.upsert(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: featureRoadmapVerdictsKeys.all });
      const allFeaturesKey = featureRoadmapVerdictsKeys.byBranch("all-features");
      const fromScratchKey = featureRoadmapVerdictsKeys.byBranch("from-scratch");
      const previousAllFeatures = qc.getQueryData<FeatureRoadmapVerdictRow[]>(allFeaturesKey);
      const previousFromScratch = qc.getQueryData<FeatureRoadmapVerdictRow[]>(fromScratchKey);

      const now = new Date().toISOString();
      const primaryRow: FeatureRoadmapVerdictRow = {
        id: `optimistic-${input.featureId}-${input.branch}`,
        feature_id: input.featureId,
        branch: input.branch,
        verdict: input.verdict,
        created_at: now,
        updated_at: now,
      };

      // 1. Primary upsert
      const primaryKey = featureRoadmapVerdictsKeys.byBranch(input.branch);
      qc.setQueryData<FeatureRoadmapVerdictRow[]>(primaryKey, (current) => {
        const base = current ?? [];
        const filtered = base.filter((v) => v.feature_id !== input.featureId);
        return [primaryRow, ...filtered];
      });

      // 2. Mirror the Postgres sync rules
      if (input.branch === "all-features" && input.verdict === "non-mvp") {
        // Demotion wipes any classification on the MVP board
        qc.setQueryData<FeatureRoadmapVerdictRow[]>(fromScratchKey, (current) =>
          (current ?? []).filter((v) => v.feature_id !== input.featureId)
        );
      } else if (input.branch === "from-scratch") {
        // Any from-scratch verdict implies (all-features, mvp)
        const syncRow: FeatureRoadmapVerdictRow = {
          id: `optimistic-${input.featureId}-all-features`,
          feature_id: input.featureId,
          branch: "all-features",
          verdict: "mvp",
          created_at: now,
          updated_at: now,
        };
        qc.setQueryData<FeatureRoadmapVerdictRow[]>(allFeaturesKey, (current) => {
          const base = current ?? [];
          const filtered = base.filter((v) => v.feature_id !== input.featureId);
          return [syncRow, ...filtered];
        });
      }

      return { previousAllFeatures, previousFromScratch };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previousAllFeatures) {
        qc.setQueryData(
          featureRoadmapVerdictsKeys.byBranch("all-features"),
          ctx.previousAllFeatures
        );
      }
      if (ctx?.previousFromScratch) {
        qc.setQueryData(
          featureRoadmapVerdictsKeys.byBranch("from-scratch"),
          ctx.previousFromScratch
        );
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: featureRoadmapVerdictsKeys.all });
    },
  });
}

export interface RemoveVerdictInput {
  featureId: string;
  branch: RoadmapBranchValue;
}

export function useRemoveVerdict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ featureId, branch }: RemoveVerdictInput) =>
      featureRoadmapVerdictsRepository.remove(featureId, branch),
    onMutate: async ({ featureId, branch }) => {
      await qc.cancelQueries({ queryKey: featureRoadmapVerdictsKeys.all });
      const branchKey = featureRoadmapVerdictsKeys.byBranch(branch);
      const previous = qc.getQueryData<FeatureRoadmapVerdictRow[]>(branchKey);
      qc.setQueryData<FeatureRoadmapVerdictRow[]>(branchKey, (current) =>
        (current ?? []).filter((v) => v.feature_id !== featureId)
      );
      return { previous };
    },
    onError: (_err, { branch }, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(featureRoadmapVerdictsKeys.byBranch(branch), ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: featureRoadmapVerdictsKeys.all });
    },
  });
}
